import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoal } from '../context/GoalContext';
import Header from '../components/Header';
import Button from '../components/Button';
import CustomSelect from '../components/CustomSelect';
import { calculateMonthly, formatCurrency } from '../utils/calculations';
import sharedStyles from './CreateGoal.module.css';
import styles from './CreateGoalStep2.module.css';

const MONTH_OPTIONS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
].map((label, i) => ({ value: String(i), label }));

const YEAR_OPTIONS = [2026, 2027, 2028, 2029, 2030].map((y) => ({
  value: String(y),
  label: String(y)
}));

const isFutureDate = (month, year) => {
  if (month === '' || !year) return false;
  const now = new Date();
  const sel = new Date(Number(year), Number(month), 1);
  return sel > new Date(now.getFullYear(), now.getMonth(), 1);
};

const toDeadlineString = (month, year) =>
  `${year}-${String(Number(month) + 1).padStart(2, '0')}-01`;

const initFromDeadline = (deadline) => {
  if (!deadline) return { month: '', year: '2027' };
  const d = new Date(deadline);
  return { month: String(d.getMonth()), year: String(d.getFullYear()) };
};

const CreateGoalStep2 = () => {
  const navigate = useNavigate();
  const { goal, setGoal } = useGoal();

  const [amount, setAmount] = useState(goal.amount || '');
  const init = initFromDeadline(goal.deadline);
  const [selectedYear, setSelectedYear] = useState(init.year);
  const [selectedMonth, setSelectedMonth] = useState(init.month);

  // Disable months that are in the past for the selected year
  const monthOptions = MONTH_OPTIONS.map((opt) => ({
    ...opt,
    disabled: !isFutureDate(opt.value, selectedYear)
  }));

  const validDate = isFutureDate(selectedMonth, selectedYear);
  const canContinue = Number(amount) > 0 && validDate;
  const monthly = canContinue
    ? calculateMonthly(Number(amount), toDeadlineString(selectedMonth, selectedYear))
    : 0;

  const handleYearChange = (year) => {
    setSelectedYear(year);
    // Clear month if it becomes invalid for the new year
    if (selectedMonth !== '' && !isFutureDate(selectedMonth, year)) {
      setSelectedMonth('');
    }
  };

  const handleContinue = () => {
    setGoal((prev) => ({
      ...prev,
      amount: Number(amount),
      deadline: toDeadlineString(selectedMonth, selectedYear)
    }));
    navigate('/create-goal/step3');
  };

  return (
    <div className={sharedStyles.screen}>
      <Header title="New goal" />

      <div className={sharedStyles.progress}>
        <div className={sharedStyles.progressTrack}>
          <div className={sharedStyles.progressFill} style={{ width: '50%' }} />
        </div>
        <span className={sharedStyles.progressLabel}>2 of 4</span>
      </div>

      <div className={sharedStyles.body}>
        <h2 className={sharedStyles.heading}>How much do you need?</h2>
        <p className={sharedStyles.sub}>Set your target amount and deadline.</p>

        {/* Amount */}
        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="goal-amount">Target amount</label>
          <div className={`${styles.amountRow} ${amount ? styles.amountRowFilled : ''}`}>
            <span className={styles.rupee}>₹</span>
            <input
              id="goal-amount"
              type="number"
              min="1"
              className={styles.amountInput}
              placeholder="6000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              aria-label="Target amount in rupees"
            />
          </div>
        </div>

        {/* Date — Year first, then Month */}
        <div className={styles.dateRow}>
          <CustomSelect
            label="Year"
            options={YEAR_OPTIONS}
            value={selectedYear}
            onChange={handleYearChange}
            placeholder="Year"
          />
          <CustomSelect
            label="Month"
            options={monthOptions}
            value={selectedMonth}
            onChange={setSelectedMonth}
            placeholder="Select month"
          />
        </div>

        {/* Live calc */}
        {canContinue && (
          <div className={styles.calcCard}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="8" cy="8" r="7" stroke="#00B8A9" strokeWidth="1.4"/>
              <path d="M8 5v4.5M8 11v.5" stroke="#00B8A9" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            <p className={styles.calcText}>
              Save <strong>{formatCurrency(monthly)}/month</strong> to reach your goal
            </p>
          </div>
        )}
      </div>

      <div className={sharedStyles.footer}>
        <Button label="Continue" onClick={handleContinue} disabled={!canContinue} />
      </div>
    </div>
  );
};

export default CreateGoalStep2;
